import { enqueueSnackbar } from 'notistack';
import { type Ref, useState, useEffect, useImperativeHandle } from 'react';
import { useForm, Controller } from 'react-hook-form';

import {
  IconCash,
  IconUsers,
  IconSchool,
  IconCalendar,
  IconAlertCircle,
} from '@tabler/icons-react';

import Input from '@/components/Input/Input';
import SectionLoader from '@/components/Loader/SectionLoader';
import Select from '@/components/Select';
import { CURRENCY_OPTIONS } from '@/constants/constant';
import { ECurrency, ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useFormatText } from '@/hooks/useFormatText';
import {
  editSubscriptionSchema,
  TEditSubscriptionFormValues,
  SUBSCRIPTION_INPUT_MAX_LENGTH,
} from '@/schema/subscriptionSchema';
import {
  getSchoolSubscription,
  editSchoolSubscription,
} from '@/service/schoolService';
import { ISchoolSubscriptionDetails } from '@/types';
import { IEditSubscriptionPayload } from '@/types/payload';
import { formatCurrency } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';

export interface IEditSubscriptionModalHandle {
  submit: () => Promise<boolean>;
}

interface IEditSubscriptionModalProps {
  schoolId?: string;
  ref?: Ref<IEditSubscriptionModalHandle>;
  onSubmitSuccess?: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

export const EditSubscriptionModal = ({
  schoolId,
  ref,
  onSubmitSuccess,
  onLoadingChange,
}: IEditSubscriptionModalProps) => {
  const { hasAccess } = useAccess();
  const { formatText } = useFormatText();
  const { formatDate } = useDateFormatter();

  const [isFetching, setIsFetching] = useState(true);
  const [subscription, setSubscription] =
    useState<ISchoolSubscriptionDetails | null>(null);

  const {
    control,
    register,
    reset,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<TEditSubscriptionFormValues>({
    mode: 'onChange',
    resolver: zodResolver(editSubscriptionSchema),
    defaultValues: {
      currency: ECurrency.KWD,
    },
  });

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!schoolId) return;
      setIsFetching(true);
      try {
        const response = await getSchoolSubscription(schoolId);
        if (response?.status && response?.data) {
          const data = response?.data as ISchoolSubscriptionDetails;
          setSubscription(data);
          reset({
            currency: data.currency,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetching(false);
      }
    };
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const licenseStats = [
    {
      label: formatText('school.schedule-renewal.school'),
      value: subscription?.schoolName ?? '-',
      icon: IconSchool,
    },
    {
      label: formatText('school.edit-subscription.start-date'),
      value: subscription?.start_date
        ? formatDate(subscription.start_date)
        : '-',
      icon: IconCalendar,
    },
    {
      label: formatText('school.schedule-renewal.expiry-date'),
      value: subscription?.end_date ? formatDate(subscription.end_date) : '-',
      icon: IconCalendar,
    },
    {
      label: formatText('school.edit-subscription.current-student-seats'),
      value: subscription?.student_limit ?? '-',
      icon: IconUsers,
    },
    {
      label: formatText('school.edit-subscription.current-amount'),
      value: subscription?.amount
        ? formatCurrency(subscription.amount, subscription.currency)
        : '-',
      icon: IconCash,
    },
  ];

  const onValidSubmit = async (
    data: TEditSubscriptionFormValues
  ): Promise<boolean> => {
    if (!schoolId) return false;

    onLoadingChange?.(true);
    try {
      const payload: IEditSubscriptionPayload = {
        school_id: schoolId,
        student_limit: data.studentLimit,
        amount: data.amount,
        transaction_id: data.transactionId,
      };
      const response = await editSchoolSubscription(payload);
      if (response?.status) {
        if (response?.message) {
          enqueueSnackbar(response.message, { variant: 'success' });
        }
        onSubmitSuccess?.();
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      onLoadingChange?.(false);
    }
  };

  const submit = async (): Promise<boolean> => {
    const isValid = await trigger();
    if (!isValid) return false;
    return onValidSubmit(getValues());
  };

  useImperativeHandle(ref, () => ({ submit }));

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  if (isFetching) {
    return <SectionLoader />;
  }

  return (
    <div className="w-full bg-white space-y-10">
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-neutral-primary">
          {formatText('school.schedule-renewal.license-details')}
        </h3>

        <div className="grid grid-cols-2 gap-y-6 gap-x-12">
          {licenseStats.map(({ label, value, icon: Icon }) => (
            <div key={label} className="flex items-start gap-3">
              <div className="mt-0.5 text-neutral-hover">
                <Icon size={20} />
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-sm leading-5 text-neutral-secondary">
                  {label}
                </span>

                <span className="text-base font-medium text-neutral-primary">
                  {value}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-neutral-primary leading-7">
          {formatText('school.edit-subscription.title')}
        </h3>

        <div className="rounded-2xl bg-yellow-50 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <IconAlertCircle size={24} className="text-yellow-600 shrink-0" />
            <p className="text-sm text-neutral-secondary">
              {formatText('school.edit-subscription.info-message')}{' '}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <Input
            label={formatText('school.edit-subscription.new-student-seats')}
            name="studentLimit"
            type="number"
            placeholder={formatText(
              'school.schedule-renewal.student-seats-placeholder'
            )}
            min={1}
            maxlength={SUBSCRIPTION_INPUT_MAX_LENGTH}
            integer
            register={register}
            error={
              errors.studentLimit?.message
                ? formatText(errors.studentLimit.message)
                : undefined
            }
            isRequired
          />

          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <Input
              label={formatText('school.edit-subscription.new-amount')}
              name="amount"
              type="number"
              step="0.001"
              placeholder={formatText(
                'school.schedule-renewal.amount-placeholder'
              )}
              min={1}
              maxlength={SUBSCRIPTION_INPUT_MAX_LENGTH}
              register={register}
              error={
                errors.amount?.message
                  ? formatText(errors.amount.message)
                  : undefined
              }
              isRequired
              className="w-full sm:flex-1"
            />
            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <Select
                  name={formatText('school.schedule-renewal.currency')}
                  placeholder={formatText(
                    'school.schedule-renewal.currency-placeholder'
                  )}
                  options={CURRENCY_OPTIONS}
                  selected={field.value ?? ''}
                  onChange={field.onChange}
                  disabled
                  mainClassName="w-full sm:flex-1"
                />
              )}
            />
          </div>

          <Input
            label={formatText('school.schedule-renewal.transaction-id')}
            name="transactionId"
            placeholder={formatText(
              'school.schedule-renewal.transaction-id-placeholder'
            )}
            register={register}
            error={
              errors.transactionId?.message
                ? formatText(errors.transactionId.message)
                : undefined
            }
            isRequired
          />
        </div>
      </div>
    </div>
  );
};
